import React, { useState } from 'react';
import AuroraBackground from '../components/AuroraBackground';
import device22 from '../assets/22.jpg';
import device115 from '../assets/115.jpg';

const PhoneIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5 text-[#30d158]" aria-hidden="true">
    <path
      d="M6.5 4h3l1.5 4-2 1.5a11 11 0 0 0 5 5L15.5 12l4 1.5v3a2 2 0 0 1-2.2 2A16 16 0 0 1 4.5 6.2 2 2 0 0 1 6.5 4z"
      stroke="currentColor"
      strokeWidth="1.6"v
      strokeLinejoin="round"
    />
  </svg>
);

const MailIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5 text-[#0a84ff]" aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.6" />
    <path d="M4 7l8 6 8-6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PinIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5 text-[#ff3b30]" aria-hidden="true">
    <path d="M12 21s7-5.5 7-11a7 7 0 1 0-14 0c0 5.5 7 11 7 11z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    <circle cx="12" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5 text-slate-500 transition-transform" aria-hidden="true">
    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ContactUs = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      question: 'Do you offer installation services?',
      answer: 'Yes! We provide complete turnkey installation solutions for all our LED signs, including shear-off poles and concrete footings to meet Australian road safety requirements. We also offer both permanent portal installations and temporary site options for short-term projects, so you can choose the solution that best fits your needs.'
    },
    {
      question: 'Is traffic data and my device data safe?',
      answer: 'Absolutely. We host all of our data on Australian-based servers exclusively—our devices only connect to our local Australian servers and databases. All data is encrypted both in transit (when being transmitted from the sign to our servers) and at rest (stored on our systems), so your sensitive traffic and device information remains fully secure.'
    },
    {
      question: 'What warranty is included with my sign?',
      answer: 'All our signs come with a minimum 1-year manufacturer warranty, with extended warranty options available through our Advanced and Premium service plans. Warranty covers manufacturing defects, not accidental damage from external sources or misuse.'
    },
    {
      question: 'Can I get a custom quote for my project?',
      answer: 'Absolutely! Call or email our office to discuss your specific requirements. We provide detailed, no-obligation quotes tailored to your project needs, whether you need a single school zone sign or a network of signs across your council region.'
    },
    {
      question: 'Are your signs compliant with Australian road standards?',
      answer: 'Yes, all our products are fully certified and compliant with Australian road safety standards. We ensure every sign meets all relevant state and federal requirements before delivery, so you can trust your signs will pass all inspections.'
    },
    {
      question: 'Can I manage multiple signs remotely?',
      answer: 'Absolutely! Our 4G-connected signs let you manage your entire fleet from a single web portal. You can update messaging, adjust brightness schedules, check battery health, and view traffic data from anywhere, eliminating the need for frequent site visits.'
    },
    {
      question: 'What power options are available for your signs?',
      answer: 'Most of our signs use solar power as standard, with built-in batteries for off-grid operation. We also offer hardwired (mains power) options for permanent sites where solar isn\'t feasible, so you can choose the power solution that works for your location.'
    },
    {
      question: 'Do you offer support after installation?',
      answer: 'Yes! We provide ongoing technical support for all our signs, with remote troubleshooting available for connected devices. Our service plans also include on-site maintenance visits if you need additional support or repairs.'
    },
    {
      question: 'Can I customise the messaging on my LED sign?',
      answer: 'Yes, all our digital LED signs let you create and schedule custom messaging through our web portal. You can display speed alerts, safety messages, community announcements, or emergency notifications, with full control over timing and content.'
    }
  ];

  return (
    <div className="relative min-h-screen overflow-hidden bg-white">
      <AuroraBackground />

      <div className="relative z-10 mx-auto max-w-[1000px] px-6 py-24 md:py-28">
        {/* Header - NO MORE max-width, spans full page width */}
        <header className="mb-14">
          <span className="mb-4 inline-block bg-[linear-gradient(135deg,#ff3b30,#30d158,#0a84ff)] bg-clip-text text-xs font-semibold uppercase tracking-[0.25em] text-transparent">
            Get in touch
          </span>
          <h1 className="text-[2.5rem] font-extrabold leading-[1.05] tracking-[-1.5px] text-[#0f172a] md:text-[3.25rem]">
            Contact Us
          </h1>
          <p className="mt-6 text-[1.05rem] leading-[1.8] text-slate-600">
            Questions about our LED speed signs or service plans? Our Australian-based team is here to
            help. We're an Australian-owned company designing and manufacturing industry-leading LED
            warning and speed signs for roads, schools, and worksites across the country.
          </p>
          <div className="mt-8 p-6 bg-slate-50 rounded-xl border border-slate-200 grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-bold text-[#0f172a] mb-3">About Our Team</h3>
              <p className="text-slate-600 leading-relaxed">
                With years of experience in LED and signage technology, we're passionate about keeping Australian
                roads safer through innovative, reliable LED sign solutions. Every product we build is
                engineered for our harsh climate, with solar-powered designs that require minimal maintenance
                and 4G connectivity for remote management. We work with local councils, transport companies,
                and educational institutions across the country to deliver signs that meet strict Australian safety standards.
              </p>
            </div>
            <div className="flex gap-4 items-center justify-center">
              <img 
                src={device22} 
                alt="LED sign project" 
                className="w-1/2 h-100 object-contain rounded-xl"
              />
              <img 
                src={device115} 
                alt="Custom LED solution" 
                className="w-1/2 h-100 object-contain rounded-xl"
              />
            </div>
          </div>
        </header>

        {/* Contact Info Grid */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 mb-12">
          {/* Customer support */}
          <div className="rounded-2xl border border-slate-200 bg-white/80 p-8 shadow-[0_8px_30px_rgba(15,23,42,0.05)] backdrop-blur-sm">
            <div className="mb-5 flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100">
                <PhoneIcon />
              </span>
              <h2 className="text-lg font-bold text-[#0f172a]">Customer Support</h2>
            </div>
            <div className="flex flex-col gap-4">
              <a
                href="tel:+0753536011"
                className="group flex items-center gap-3 text-[1.02rem] text-slate-600 transition-colors hover:text-[#0f172a]"
              >
                <PhoneIcon />
                (07) 5353 6011
              </a>
              <a
                href="mailto:info@ledspeedsigns.com.au"
                className="group flex items-center gap-3 text-[1.02rem] text-slate-600 transition-colors hover:text-[#0f172a]"
              >
                <MailIcon />
                info@ledspeedsigns.com.au
              </a>
            </div>
            
            <p className="mt-6 pt-6 border-t border-slate-200 text-sm italic text-slate-400">
              Business hours: Monday–Thursday 7:00AM – 4:00PM | Friday 7:00AM – 1:00PM AEST
            </p>
          </div>

          {/* Registered office */}
          <div className="rounded-2xl border border-slate-200 bg-white/80 p-8 shadow-[0_8px_30px_rgba(15,23,42,0.05)] backdrop-blur-sm">
            <div className="mb-5 flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100">
                <PinIcon />
              </span>
              <h2 className="text-lg font-bold text-[#0f172a]">Registered Office</h2>
            </div>
            <address className="not-italic text-[1.02rem] leading-[1.9] text-slate-600">
              LED Speed signs
              <br />
              2/16 Aerodrome Road
              <br />
              Caboolture QLD 4510
              <br />
              Australia
            </address>
          </div>
        </div>

        {/* FULL-WIDTH MAP - larger height, spans entire page width */}
        <div className="w-full rounded-2xl border border-slate-200 bg-white/80 overflow-hidden shadow-[0_8px_30px_rgba(15,23,42,0.05)] backdrop-blur-sm mb-12">
          <div className="p-8 border-b border-slate-200">
            <h2 className="text-lg font-bold text-[#0f172a]">Find Us On Map</h2>
          </div>
          <iframe 
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3306.556633434105!2d153.0542833152108!3d-27.090867!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6b1a1887f48a3387%3A0x7a2b8d6c5f4e3a1b!2s2%2F16%20Aerodrome%20Rd%2C%20Caboolture%20QLD%204510%2C%20Australia!5e0!3m2!1sen!2sau!4v1735790000000"
            width="100%" 
            height="450" 
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            title="Office Location Map"
            className="grayscale hover:grayscale-0 transition-all duration-500"
          />
        </div>

        {/* FAQ Section */}
        <div className="rounded-2xl border border-slate-200 bg-white/80 p-8 shadow-[0_8px_30px_rgba(15,23,42,0.05)] backdrop-blur-sm">
          <h2 className="text-2xl font-bold text-[#0f172a] mb-6">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="border-b border-slate-100 pb-4 last:border-0 last:pb-0">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between text-left gap-4"
                >
                  <h3 className="text-lg font-medium text-[#0f172a]">{faq.question}</h3>
                  <span className={`transition-transform duration-300 ${openFaq === index ? 'rotate-180' : ''}`}>
                    <ChevronDownIcon />
                  </span>
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${openFaq === index ? 'max-h-40 mt-3' : 'max-h-0'}`}>
                  <p className="text-slate-600 leading-relaxed">{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;